'use client';

import React from 'react';
import Link from 'next/link';
import { motion, useInView } from 'framer-motion';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Service {
  id: number;
  title: string;
  description: string;
  image: string;
  alt: string;
  icon: string;
  category: string;
  features: string[];
}

const ServiceCards = () => {
  const ref = React.useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  const services: Service[] = [
  {
    id: 1,
    title: 'Health Checkup Packages',
    description: 'Comprehensive health screening at relief rates with 40+ parameters including CBC, lipid profile, liver function, kidney function, and thyroid tests.',
    image: "https://images.unsplash.com/photo-1666886573600-61c634663827",
    alt: 'Medical professional in white coat examining patient health reports with stethoscope on desk',
    icon: 'HeartIcon',
    category: 'Healthcare',
    features: ['40+ Parameters', 'Relief Rates', 'Quick Reports']
  },
  {
    id: 2,
    title: 'Community Health Camps',
    description: 'Free health screening camps in Sarthana, Varachha, and Kadodara with basic checkups, blood pressure monitoring, and health consultations.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18e9d8416-1764661115359.png",
    alt: 'Community health workers conducting outdoor medical camp with patients waiting in line under tent',
    icon: 'UserGroupIcon',
    category: 'Community',
    features: ['Free Screening', 'Local Areas', 'Expert Doctors']
  },
  {
    id: 3,
    title: 'Clothing Distribution',
    description: 'Dignified clothing assistance for underprivileged families with seasonal clothing drives and emergency support for those in need.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_15d4d31df-1767816289306.png",
    alt: 'Volunteers organizing neatly folded colorful clothes on tables during community donation drive',
    icon: 'GiftIcon',
    category: 'Community',
    features: ['Seasonal Drives', 'All Ages', 'Quality Items']
  },
  {
    id: 4,
    title: 'Food Support Programs',
    description: 'Nutritious meal distribution and food assistance for families facing economic hardship with weekly distribution schedules.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1169b2ada-1764663453913.png",
    alt: 'Volunteers serving hot meals in containers to community members during food distribution event',
    icon: 'CakeIcon',
    category: 'Community',
    features: ['Weekly Distribution', 'Nutritious Meals', 'Family Support']
  },
  {
    id: 5,
    title: 'Fitness & Wellness',
    description: 'Free yoga sessions, fitness classes, and wellness workshops promoting holistic health and mental well-being in the community.',
    image: "https://images.unsplash.com/photo-1687436874044-2c593fbf0245",
    alt: 'Group of people practicing yoga poses on mats in outdoor park setting during morning session',
    icon: 'SparklesIcon',
    category: 'Wellness',
    features: ['Free Classes', 'Expert Trainers', 'All Levels']
  },
  {
    id: 6,
    title: 'Volunteer Opportunities',
    description: 'Join our mission to serve the community through various volunteer programs including health camps, events, and community outreach.',
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_174401344-1766923077310.png",
    alt: 'Diverse group of smiling volunteers wearing matching t-shirts standing together with raised hands',
    icon: 'HandRaisedIcon',
    category: 'Engagement',
    features: ['Flexible Hours', 'Training Provided', 'Make Impact']
  }];


  return (
    <section ref={ref} className="py-12 md:py-20 bg-background">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-3xl mx-auto mb-8 md:mb-16"
        >
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            Our Services & Programs
          </h2>
          <p className="text-lg text-muted-foreground">
            Comprehensive healthcare and community support services designed to uplift and empower
            families across Surat with dignity and compassion.
          </p>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: {
                staggerChildren: 0.15,
                delayChildren: 0.2,
              },
            },
          }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8"
        >
          {services.map((service) =>
          <motion.div
            key={service.id}
            variants={{
              hidden: { opacity: 0, y: 50 },
              visible: { opacity: 1, y: 0 },
            }}
            whileHover={{ scale: 1.03, y: -8 }}
            transition={{ duration: 0.3 }}
            className="glass-card rounded-2xl overflow-hidden shadow-glass hover:shadow-elevated transition-all duration-300 group">

              <div className="relative h-56 overflow-hidden">
                <AppImage
                src={service.image}
                alt={service.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-primary/90 glass-button">
                  <span className="text-xs font-semibold text-primary-foreground">
                    {service.category}
                  </span>
                </div>
                <div className="absolute top-4 right-4 w-10 h-10 rounded-full glass flex items-center justify-center">
                  <Icon name={service.icon as any} size={20} className="text-primary" />
                </div>
              </div>

              <div className="p-6 space-y-4">
                <h3 className="text-xl font-heading font-bold text-foreground group-hover:text-primary transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed line-clamp-3">
                  {service.description}
                </p>

                <div className="flex flex-wrap gap-2">
                  {service.features.map((feature, index) =>
                <span
                  key={index}
                  className="px-3 py-1 rounded-full bg-muted text-xs font-medium text-foreground">

                      {feature}
                    </span>
                )}
                </div>

                <Link
                href="/services"
                className="inline-flex items-center space-x-2 text-primary font-semibold hover:text-primary/80 transition-colors duration-300 group/link">

                  <span>Learn More</span>
                  <Icon
                  name="ArrowRightIcon"
                  size={16}
                  className="group-hover/link:translate-x-1 transition-transform duration-300" />

                </Link>
              </div>
            </motion.div>
          )}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.6, delay: 1 }}
          className="text-center mt-12"
        >
          <motion.div whileHover={{ scale: 1.05, y: -3 }} whileTap={{ scale: 0.95 }}>
            <Link
              href="/services"
              className="inline-flex items-center space-x-2 px-8 py-4 rounded-xl font-heading font-semibold text-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-cta hover:shadow-elevated">

              <span>View All Services</span>
              <Icon name="ArrowRightIcon" size={20} />
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>);

};

export default ServiceCards;